import type { GeoLocation } from '@bambi/api-client/schemas';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';

import { GeolocationPickerListItem } from './GeolocationPickerListItem';
import { GeolocationEmptyState } from 'src/features/settings/geolocations/GeolocationEmptyState';
import {
  GeolocationSearch,
  usePaginatedGeolocationSearchResults,
} from 'src/features/settings/geolocations/management/GeolocationSearch';

export type OnSelectGeolocationHandler = (
  selectedGeolocation: GeoLocation
) => void;

interface GeolocationPickerListProps {
  onSelect: OnSelectGeolocationHandler;
}

export function GeolocationPickerList({
  onSelect,
}: GeolocationPickerListProps) {
  const {
    geolocationsListQueryState: { geolocations, isLoading, isFetching },
    searchIsActive,
  } = usePaginatedGeolocationSearchResults({ enableArchived: false });

  return (
    <div className="space-y-4" data-testid="geolocation-picker-list">
      <GeolocationSearch shouldAutoFocus />
      <ResultsContent
        searchIsActive={searchIsActive}
        showLoadingIndicator={isLoading || isFetching}
        geolocations={geolocations}
        onSelect={onSelect}
      />
    </div>
  );
}

interface ResultsContentProps {
  searchIsActive: boolean;
  showLoadingIndicator: boolean;
  geolocations: GeoLocation[];
  onSelect: OnSelectGeolocationHandler;
}

function ResultsContent({
  searchIsActive,
  showLoadingIndicator,
  geolocations,
  onSelect,
}: ResultsContentProps) {
  if (showLoadingIndicator) {
    return <LoadingIndicator />;
  }

  if (geolocations.length === 0) {
    return <GeolocationEmptyState searchIsActive={searchIsActive} />;
  }

  return (
    <div className="space-y-2">
      {geolocations.map((geolocation) => (
        <GeolocationPickerListItem
          key={geolocation.id}
          geolocation={geolocation}
          onSelect={() => onSelect(geolocation)}
        />
      ))}
    </div>
  );
}
